export const ENROLLMENTS_CHANGED = 'idcoder-enrollments-changed';

export type EnrollmentKind = 'courses' | 'projects' | 'events' | 'gigs';

type EnrollmentRecord = { id: number };

const lists: Record<EnrollmentKind, EnrollmentRecord[] | null> = {
  courses: null,
  projects: null,
  events: null,
  gigs: null,
};

const loaders: Record<EnrollmentKind, Promise<EnrollmentRecord[]> | null> = {
  courses: null,
  projects: null,
  events: null,
  gigs: null,
};

function notify() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ENROLLMENTS_CHANGED));
  }
}

export function getCachedEnrollments<T extends EnrollmentRecord>(kind: EnrollmentKind): T[] | null {
  return lists[kind] as T[] | null;
}

export function setCachedEnrollments<T extends EnrollmentRecord>(kind: EnrollmentKind, items: T[]) {
  lists[kind] = items;
  loaders[kind] = Promise.resolve(items);
}

export function rememberMyEnrollments<T extends EnrollmentRecord>(kind: EnrollmentKind, loader: () => Promise<T[]>) {
  const cached = lists[kind] as T[] | null;
  if (cached) return Promise.resolve(cached);
  if (!loaders[kind]) {
    loaders[kind] = loader()
      .then((items) => {
        lists[kind] = items;
        return items;
      })
      .catch((error) => {
        loaders[kind] = null;
        throw error;
      });
  }
  return loaders[kind] as Promise<T[]>;
}

export function upsertMyEnrollment<T extends EnrollmentRecord>(kind: EnrollmentKind, item: T) {
  const current = (lists[kind] as T[] | null) || [];
  const index = current.findIndex((row) => row.id === item.id);
  const next = index >= 0
    ? current.map((row) => (row.id === item.id ? { ...row, ...item } : row))
    : [item, ...current];
  lists[kind] = next;
  loaders[kind] = Promise.resolve(next);
  notify();
}

export function invalidateMyEnrollments(kind?: EnrollmentKind) {
  const kinds: EnrollmentKind[] = kind ? [kind] : ['courses', 'projects', 'events', 'gigs'];
  kinds.forEach((key) => {
    lists[key] = null;
    loaders[key] = null;
  });
  notify();
}

export async function refreshMyEnrollments<T extends EnrollmentRecord>(kind: EnrollmentKind, loader: () => Promise<T[]>) {
  loaders[kind] = null;
  const items = await loader();
  lists[kind] = items;
  loaders[kind] = Promise.resolve(items);
  notify();
  return items;
}
